import React, { useMemo } from 'react';
import { Task, TaskStatus, Quadrant } from '../types';
import { Brain, Trophy, Flame, Target, Zap } from 'lucide-react';
import { QUADRANT_COLORS } from '../constants';

interface AnalyticsViewProps {
  tasks: Task[];
  isPro: boolean;
  onUpgrade?: () => void;
}

export const AnalyticsView: React.FC<AnalyticsViewProps> = ({ tasks, isPro, onUpgrade }) => {
  // --- DATA PROCESSING ---
  
  // 1. Quadrant Distribution (All active + Completed recently)
  const quadrantStats = useMemo(() => {
    const counts = {
      [Quadrant.Do]: 0,
      [Quadrant.Schedule]: 0,
      [Quadrant.Delegate]: 0,
      [Quadrant.Eliminate]: 0
    };
    
    let total = 0;
    tasks.forEach(t => {
      // Include all tasks to show "Load", not just "Done"
      if (t.status !== TaskStatus.Done || (t.completedAt && Date.now() - t.completedAt < 7 * 24 * 60 * 60 * 1000)) {
        counts[t.quadrant]++;
        total++;
      }
    });
    
    return { counts, total: total || 1 }; // Avoid div by zero
  }, [tasks]);

  // 2. Weekly Activity (Completed Tasks per Day)
  const weeklyStats = useMemo(() => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const today = new Date();
    const stats = new Array(7).fill(0);
    const labels = new Array(7).fill('');

    // Initialize labels for last 7 days
    for (let i = 6; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      labels[6 - i] = days[d.getDay()];
    }

    tasks.forEach(t => {
      if (t.status === TaskStatus.Done && t.completedAt) {
        const diffTime = Math.abs(today.getTime() - t.completedAt);
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)); 
        if (diffDays < 7) {
          stats[6 - diffDays]++;
        }
      }
    });

    const max = Math.max(...stats, 4); // Min height of 4 for scale
    return { data: stats, labels, max };
  }, [tasks]);

  // 3. Focus Time Total
  const totalFocusMinutes = useMemo(() => {
    const seconds = tasks.reduce((acc, t) => acc + (t.timeSpent || 0), 0);
    return Math.floor(seconds / 60);
  }, [tasks]);


  // --- AI COACH LOGIC (Heuristics) ---
  const getAiInsight = () => {
    const q1Pct = (quadrantStats.counts[Quadrant.Do] / quadrantStats.total) * 100;
    const q2Pct = (quadrantStats.counts[Quadrant.Schedule] / quadrantStats.total) * 100;
    const q4Pct = (quadrantStats.counts[Quadrant.Eliminate] / quadrantStats.total) * 100;
    
    if (tasks.length < 5) {
      return {
        title: "Just Getting Started",
        msg: "Add more tasks to unlock personalized insights. Try the 'Magic Sort' feature!",
        icon: <Target className="text-blue-500" />
      };
    }

    if (q4Pct > 20) {
      return {
        title: "Distraction Alert",
        msg: "Over 20% of your tasks are in 'Eliminate'. Consider deleting them to free up mental space.",
        icon: <Zap className="text-yellow-500" />
      };
    }

    if (q1Pct > 60) {
      return {
        title: "Firefighting Mode",
        msg: "You're spending too much time on Urgent tasks. Try to plan ahead to move more into 'Schedule'.",
        icon: <Flame className="text-red-500" />
      };
    }

    if (q2Pct > 40) {
      return {
        title: "The Strategist",
        msg: "Great job! You are focusing heavily on long-term goals (Quadrant 2). Keep it up!",
        icon: <Trophy className="text-blue-500" />
      };
    }

    return {
      title: "Balanced Workflow",
      msg: "Your task distribution looks healthy. Remember to use Focus Mode for deep work.",
      icon: <Brain className="text-purple-500" />
    };
  };

  const insight = getAiInsight();

  // --- SVG HELPERS ---
  const renderDonut = () => {
    const size = 100;
    const strokeWidth = 15;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    let offset = 0;

    const colors = {
      [Quadrant.Do]: '#ef4444',       // red-500
      [Quadrant.Schedule]: '#3b82f6', // blue-500
      [Quadrant.Delegate]: '#eab308', // yellow-500
      [Quadrant.Eliminate]: '#10b981' // emerald-500
    };

    return (
      <svg width="100%" height="100%" viewBox={`0 0 ${size} ${size}`} className="transform -rotate-90">
        {Object.values(Quadrant).map(q => {
          const count = quadrantStats.counts[q];
          const pct = count / quadrantStats.total;
          const strokeLength = circumference * pct;
          const currentOffset = offset;
          offset -= strokeLength;

          if (count === 0) return null;

          return (
            <circle
              key={q}
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="transparent"
              stroke={colors[q]}
              strokeWidth={strokeWidth}
              strokeDasharray={`${strokeLength} ${circumference}`}
              strokeDashoffset={currentOffset}
              className="transition-all duration-1000 ease-out"
            />
          );
        })}
        {/* Inner Text */}
        <text 
            x="50%" 
            y="50%" 
            dominantBaseline="middle" 
            textAnchor="middle" 
            transform={`rotate(90 ${size/2} ${size/2})`}
            className="text-[10px] fill-gray-500 font-medium"
        >
            {quadrantStats.total} Tasks
        </text>
      </svg>
    );
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in pb-10">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-1 flex items-center gap-2">
          <ActivityIcon /> Productivity Pulse
        </h2>
        <p className="text-gray-500">Insights into your work habits and focus trends.</p>
      </div>

      {/* --- TOP CARDS --- */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Insight Card (AI Coach) */}
        <div className="bg-white p-6 rounded-xl border border-blue-100 shadow-sm col-span-1 md:col-span-2 relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4 opacity-5">
                <Brain size={120} />
            </div>
            <div className="relative z-10 flex gap-4 items-start">
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                    {insight.icon}
                </div>
                <div>
                    <h3 className="font-bold text-gray-900 text-lg mb-1">{insight.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed max-w-md">
                        {insight.msg}
                    </p>
                </div>
            </div>
        </div>

        {/* Focus Time Card */}
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex flex-col justify-center items-center text-center">
            <div className="text-gray-500 text-sm font-medium uppercase tracking-wider mb-2">Total Focus Time</div>
            <div className="text-4xl font-mono font-bold text-gray-900">
                {Math.floor(totalFocusMinutes / 60)}<span className="text-lg text-gray-400">h</span> {totalFocusMinutes % 60}<span className="text-lg text-gray-400">m</span>
            </div>
            <div className="text-xs text-green-600 mt-2 bg-green-50 px-2 py-1 rounded-full">
                Lifetime Stats
            </div>
        </div>
      </div>

      {/* --- CHARTS ROW --- */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          
          {/* 1. Matrix Harmony (Donut) */}
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
            <h3 className="font-semibold text-gray-800 mb-6">Matrix Harmony</h3>
            <div className="flex items-center justify-around">
                <div className="w-40 h-40 relative">
                    {renderDonut()}
                </div>
                <div className="space-y-3 text-sm">
                    {Object.values(Quadrant).map(q => (
                        <div key={q} className="flex items-center gap-2">
                            <div className={`w-3 h-3 rounded-full ${QUADRANT_COLORS[q].split(' ')[0]}`}></div>
                            <span className="text-gray-600 capitalize">{q.toLowerCase()}</span>
                            <span className="font-bold text-gray-900 ml-auto">{quadrantStats.counts[q]}</span>
                        </div>
                    ))}
                </div>
            </div>
          </div>

          {/* 2. Weekly Activity (Bar) */}
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex flex-col">
             <div className="flex justify-between items-center mb-6">
                <h3 className="font-semibold text-gray-800">Weekly Completion</h3>
                {!isPro && <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-1 rounded">Last 7 Days</span>}
             </div>
             
             <div className="flex-1 flex items-end justify-between gap-2 h-40 pt-4">
                {weeklyStats.data.map((val, idx) => {
                    const heightPct = (val / weeklyStats.max) * 100;
                    return (
                        <div key={idx} className="flex flex-col items-center gap-2 flex-1 group relative">
                            <div className="relative w-full flex items-end justify-center h-full bg-gray-50 rounded-t-md overflow-hidden">
                                <div 
                                    style={{ height: `${heightPct}%` }} 
                                    className="w-full bg-blue-500 opacity-80 group-hover:opacity-100 transition-all rounded-t-sm"
                                ></div>
                            </div>
                            <span className="text-xs text-gray-400 font-medium">{weeklyStats.labels[idx]}</span>
                            
                            {/* Tooltip */}
                            <div className="absolute -top-8 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                                {val} Tasks
                            </div>
                        </div>
                    );
                })}
             </div>
          </div>

      </div>

      {!isPro && (
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white p-6 rounded-xl text-center">
            <h3 className="font-bold text-lg mb-2">Unlock Historical Data 📈</h3>
            <p className="text-gray-300 text-sm mb-4 max-w-lg mx-auto">
                Free plan users see the last 7 days. Upgrade to Pro to view your entire productivity history and unlock advanced trends.
            </p>
            <button 
                onClick={onUpgrade}
                className="bg-white text-gray-900 px-6 py-2 rounded-full font-bold text-sm hover:bg-gray-100 transition-colors"
            >
                Start 7-Day Free Trial
            </button>
        </div>
      )}
    </div>
  );
};

// Simple Icon wrapper
const ActivityIcon = () => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      width="24" 
      height="24" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className="text-blue-600"
    >
        <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
    </svg>
);